import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BellRing, Check, Loader2, MapPin, Volume2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SILENCE_MS = 120000;
const CHIME_MS = 3000;

// A short double beep, built in the browser so there is no audio file to load and nothing to wait
// for. Phones block sound until the person has touched the page at least once, so if the context
// will not start we ask for one tap instead of pretending the alarm is sounding.
const useChime = (active) => {
  const ctxRef = useRef(null);
  const [blocked, setBlocked] = useState(false);

  const beep = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state !== "running") return;
    [0, 0.28].forEach((offset) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = offset ? 740 : 988;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offset + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.24);
    });
    navigator.vibrate?.([400, 150, 400]);
  }, []);

  const start = useCallback(async () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      ctxRef.current = ctxRef.current || new Ctx();
      if (ctxRef.current.state === "suspended") await ctxRef.current.resume();
      setBlocked(ctxRef.current.state !== "running");
      beep();
    } catch {
      setBlocked(true);
    }
  }, [beep]);

  useEffect(() => {
    if (!active) return undefined;
    start();
    const timer = setInterval(beep, CHIME_MS);
    return () => clearInterval(timer);
  }, [active, start, beep]);

  return { blocked, enable: start };
};

/** A job offer nobody has answered takes over the screen and keeps chiming. */
export const JobOfferAlarm = ({ offers, onAnswered }) => {
  const [silencedUntil, setSilencedUntil] = useState(0);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const offer = offers[0];

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, []);

  // A new offer always re-opens the takeover, even if the last one was silenced.
  useEffect(() => { setSilencedUntil(0); setError(""); }, [offer?.id]);

  const silenced = silencedUntil > now;
  const { blocked, enable } = useChime(Boolean(offer) && !silenced);
  const line = useMemo(() => [offer?.pickup_time, (offer?.pickup?.address || "").split(",")[0],
                              offer?.customer_name].filter(Boolean).join(" · "), [offer]);

  if (!offer) return null;

  const respond = async (action) => {
    setBusy(action);
    setError("");
    try {
      await api.post(`/driver/trips/${offer.id}/respond`, { action });
      await onAnswered?.();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  if (silenced) {
    return (
      <button data-testid="job-offer-bar" onClick={() => setSilencedUntil(0)}
        className="fixed inset-x-0 bottom-[4.5rem] z-50 flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-ink-900 shadow-lg shadow-black/40">
        <BellRing className="h-4 w-4 shrink-0" /> Job #{offer.number} still needs an answer — tap
      </button>
    );
  }

  return (
    <div data-testid="job-offer-alarm"
      className="fixed inset-0 z-50 flex flex-col justify-center gap-6 bg-ink-900/95 px-6 py-10 backdrop-blur-md">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div className="space-y-2">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400">
            <BellRing className="h-4 w-4 animate-pulse" /> New job offered to you
          </p>
          <p data-testid="job-offer-number" className="font-display text-4xl text-slate-50">
            #{offer.number}
          </p>
          <p data-testid="job-offer-line" className="text-base text-slate-300">{line}</p>
        </div>

        <div className="space-y-3 rounded-2xl border border-ink-600 bg-ink-800 p-4">
          {[["Pick-up", offer.pickup?.address], ["Drop-off", offer.dropoff?.address]].map(
            ([label, value]) => (
              <div key={label} className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
                  <p className="text-sm text-slate-200">{value || "—"}</p>
                </div>
              </div>
            ))}
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            {offer.trip_date} · {offer.passengers || 1} passenger(s)
            {offer.vehicle_name ? ` · ${offer.vehicle_name}` : ""}
          </p>
        </div>

        {blocked && (
          <Button data-testid="job-offer-enable-sound" variant="outline" onClick={enable}
            className="w-full border-amber-500/40 text-amber-300">
            <Volume2 className="mr-2 h-4 w-4" /> Tap once to turn the alert sound on
          </Button>
        )}
        {error && <p data-testid="job-offer-error" className="text-sm text-red-400">{error}</p>}

        <div className="grid gap-3">
          <Button data-testid="job-offer-accept" disabled={busy !== ""} onClick={() => respond("accept")}
            className="h-14 bg-emerald-500 text-base text-ink-900 hover:bg-emerald-400">
            {busy === "accept" ? <Loader2 className="h-5 w-5 animate-spin" />
              : <><Check className="mr-2 h-5 w-5" /> ACCEPT JOB</>}
          </Button>
          <Button data-testid="job-offer-decline" disabled={busy !== ""} variant="outline"
            onClick={() => respond("decline")}
            className="h-12 border-red-500/40 text-red-300 hover:bg-red-500/10">
            {busy === "decline" ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><X className="mr-2 h-4 w-4" /> Can't take it</>}
          </Button>
          <button data-testid="job-offer-silence" onClick={() => setSilencedUntil(Date.now() + SILENCE_MS)}
            className="font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-300">
            Silence for two minutes — I'm driving
          </button>
        </div>
      </div>
    </div>
  );
};
